import { useCallback, useEffect, useState, useMemo } from 'react';
import { Box, Button, Grid, IconButton } from '@mui/material';
import { Add as AddIcon, Block as BlockIcon, Check as CheckIcon } from '@mui/icons-material';
import NewWcnTable, { DataTableColumn } from '@/components/datatable/NewWcnTable.tsx';
import EditorSidebar from '@/components/EditorSidebar.tsx';
import RfidTagProductEditor from './components/products/RfidTagProductEditor';
import RfidTagProductTypeEditor from './components/products/RfidTagProductTypeEditor';
import { useTranslation } from 'react-i18next';
import { AppCard, NewPageTitleBar } from '@/components';
import { getMenuData } from '@core/data/menuData.ts';
import { productService } from '@/services';
import { useNotifications } from '@core/hooks/useNotifications.ts';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import { useApiStore } from '@core/store';
import { Product, ProductType } from '@core/store/slices/apiSlice.ts';
import { DateTime } from 'luxon';

const Products: FCSecurePage = () => {
    const { t } = useTranslation();
    const { notifySuccess, notifyErrorResponse } = useNotifications();
    const [productTypes, setProductTypes] = useState<ProductType[]>([]);
    const [selected, setSelected] = useState<Product | ProductType | null>(null);
    const [selectedKind, setSelectedKind] = useState<'product' | 'type' | null>(null);
    const { rfidTagProducts, loadRfidTagProducts } = useApiStore();
    const [loading, setLoading] = useState(false);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [types] = await Promise.all([productService.loadRfidTagProductTypes(), loadRfidTagProducts()]);
            setProductTypes(types || []);
            setLastUpdate(DateTime.now());
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useRegisterRefresh(load, loading);

    useEffect(() => {
        load();
    }, [load]);

    const openEditor = useCallback((kind: 'product' | 'type', item?: Product | ProductType) => {
        setSelectedKind(kind);
        setSelected(
            item ||
                (kind === 'product'
                    ? {
                          id: null,
                          name: '',
                          articleNumber: '',
                          productTypeId: '',
                          productType: undefined,
                          disabled: false
                      }
                    : { id: null, typeName: '' })
        );
    }, []);

    const closeEditor = useCallback(() => {
        setSelected(null);
        setSelectedKind(null);
    }, []);

    const saveItem = useCallback(async (kind: 'product' | 'type', item: Product | ProductType) => {
        setLoading(true);
        try {
            if (kind === 'product') {
                const prod = item as Product;
                if (!prod.id) {
                    await productService.createRfidTagProduct(prod);
                } else {
                    await productService.updateRfidTagProduct(prod.id, prod);
                }
            } else {
                const type = item as ProductType;
                if (!type.id) {
                    await productService.createRfidTagProductType(type);
                } else {
                    await productService.updateRfidTagProductType(type.id, type);
                }
            }
            notifySuccess();
            await load();
            closeEditor();
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setLoading(false);
        }
    }, []);

    const toggleDisabled = useCallback(
        (product: Product) => {
            saveItem('product', { ...product, disabled: !product.disabled });
        },
        [saveItem]
    );

    const productColumns: DataTableColumn<Product>[] = useMemo(
        () => [
            {
                text: t('name'),
                value: 'name',
                width: 300,
                render: (row) => (
                    <span style={{ textDecoration: row.disabled ? 'line-through' : 'none' }}>{row.name}</span>
                )
            },
            { text: t('articleNumber'), value: 'articleNumber', width: 200 },
            {
                text: t('productType'),
                value: 'productType',
                width: 300,
                render: (row) => row.productType?.typeName || '-'
            },
            {
                text: t('actions'),
                value: 'actions',
                width: 100,
                align: 'left',
                sortable: false,
                isAction: true,
                render: (row) => (
                    <>
                        <IconButton size="small" onClick={() => openEditor('product', row)}>
                            <i className="bi bi-pencil" />
                        </IconButton>
                        <IconButton size="small" onClick={() => toggleDisabled(row)}>
                            {row.disabled ? <CheckIcon /> : <BlockIcon />}
                        </IconButton>
                    </>
                )
            }
        ],
        []
    );

    const productTypeColumns: DataTableColumn<ProductType>[] = useMemo(
        () => [
            { text: t('name'), value: 'typeName', width: 200 },
            {
                text: t('actions'),
                value: 'actions',
                width: 100,
                align: 'left',
                sortable: false,
                isAction: true,
                render: (row) => (
                    <IconButton size="small" onClick={() => openEditor('type', row)}>
                        <i className="bi bi-pencil" />
                    </IconButton>
                )
            }
        ],
        []
    );

    return (
        <Box>
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-settings" />}
                title={t('products')}
                menus={getMenuData()}
                currentRoute={{ path: '/manage-organisation/products' }}
                iconSize="20px"
                t={t}
                lastUpdate={lastUpdate}
            />

            <Box sx={{ width: '100%', px: 3, mt: 2 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <AppCard
                            icon="wifi"
                            heading={t('products')}
                            iconClass="rotated-180"
                            customButtons={true}
                            customButtonsSlot={
                                <Button
                                    variant="text"
                                    color="inherit"
                                    startIcon={<AddIcon />}
                                    onClick={() => openEditor('product')}
                                    sx={{ textTransform: 'none' }}
                                    disabled={loading}
                                >
                                    {t('add')}
                                </Button>
                            }
                        >
                            <Box sx={{ mb: 4 }}>
                                <NewWcnTable
                                    columns={productColumns}
                                    data={rfidTagProducts}
                                    loading={loading}
                                    noDataMessage={t('noData')}
                                    size="small"
                                    pagination={{
                                        count: rfidTagProducts.length,
                                        rowsPerPageOptions: [5, 10, 15]
                                    }}
                                />
                            </Box>
                        </AppCard>
                    </Grid>

                    <Grid item xs={12}>
                        <AppCard
                            icon="wifi"
                            heading={t('types')}
                            iconClass="rotated-180"
                            customButtons={true}
                            customButtonsSlot={
                                <Button
                                    variant="text"
                                    color="inherit"
                                    startIcon={<AddIcon />}
                                    onClick={() => openEditor('type')}
                                    sx={{ textTransform: 'none' }}
                                    disabled={loading}
                                >
                                    {t('add')}
                                </Button>
                            }
                        >
                            <Box>
                                <NewWcnTable
                                    columns={productTypeColumns}
                                    data={productTypes}
                                    loading={loading}
                                    noDataMessage={t('noData')}
                                    size="small"
                                    pagination={{
                                        count: productTypes.length,
                                        rowsPerPageOptions: [5, 10, 15]
                                    }}
                                />
                            </Box>
                        </AppCard>
                    </Grid>
                </Grid>
                <EditorSidebar open={!!selected} onClose={closeEditor}>
                    {selectedKind === 'product' && selected && (
                        <RfidTagProductEditor
                            item={selected as Product}
                            itemTypes={productTypes}
                            onSave={(item) => saveItem('product', item)}
                            onCancel={closeEditor}
                        />
                    )}
                    {selectedKind === 'type' && selected && (
                        <RfidTagProductTypeEditor
                            item={selected as ProductType}
                            onSave={(item) => saveItem('type', item)}
                            onCancel={closeEditor}
                        />
                    )}
                </EditorSidebar>
            </Box>
        </Box>
    );
};

export default Products;
